import { useCallback, type ReactNode } from 'react';
import { DatePicker } from '../DatePicker/index.js';
import { TimePicker, type TimePickerValue, type TimeFormat } from '../TimePicker/index.js';
import { useControllableState } from '../../hooks/useControllableState.js';
import type { PlainDate } from '../Calendar/Calendar.js';

export interface DateTimeValue {
  date: PlainDate | null;
  time: TimePickerValue;
}

export interface DateTimePickerProps {
  value?: DateTimeValue;
  defaultValue?: DateTimeValue;
  onChange?: (value: DateTimeValue) => void;
  format?: TimeFormat;
  /** Show seconds column in the time picker. Default false. */
  showSeconds?: boolean;
  /** Minute granularity. Default 1. */
  minuteStep?: number;
  disabled?: boolean;
  invalid?: boolean;
  size?: 'sm' | 'md' | 'lg';
  locale?: string;
  placeholder?: string;
  'aria-label'?: string;
  className?: string;
}

const DEFAULT_TIME: TimePickerValue = { hour: 9, minute: 0 };
const DEFAULT_VALUE: DateTimeValue = { date: null, time: DEFAULT_TIME };

/** Combined date + time picker. Spec 15 §5. Composed from the existing
 *  DatePicker + TimePicker — no new overlay surface; the two controls sit
 *  side-by-side and share a single controlled value shape so callers only
 *  deal with one `onChange` event. */
export function DateTimePicker(props: DateTimePickerProps): ReactNode {
  const {
    value,
    defaultValue = DEFAULT_VALUE,
    onChange,
    format = '24',
    showSeconds = false,
    minuteStep = 1,
    disabled = false,
    invalid,
    size = 'md',
    locale,
    placeholder,
    className,
  } = props;

  const [current, setCurrent] = useControllableState<DateTimeValue>({
    defaultValue,
    ...(value !== undefined ? { value } : {}),
    ...(onChange !== undefined ? { onChange } : {}),
  });

  const onDateChange = useCallback(
    (d: PlainDate | null): void => {
      setCurrent({ date: d, time: current.time });
    },
    [current.time, setCurrent],
  );
  const onTimeChange = useCallback(
    (t: TimePickerValue): void => {
      setCurrent({ date: current.date, time: t });
    },
    [current.date, setCurrent],
  );

  const classes = ['vq-datetime-picker', className].filter(Boolean).join(' ');

  return (
    <div
      className={classes}
      data-component="DateTimePicker"
      data-size={size}
      data-state={disabled ? 'disabled' : invalid === true ? 'invalid' : 'idle'}
    >
      <DatePicker
        value={current.date}
        onChange={onDateChange}
        size={size}
        disabled={disabled}
        {...(invalid !== undefined ? { invalid } : {})}
        {...(locale !== undefined ? { locale } : {})}
        {...(placeholder !== undefined ? { placeholder } : {})}
        {...(props['aria-label'] !== undefined ? { 'aria-label': props['aria-label'] } : {})}
      />
      <TimePicker
        value={current.time}
        onChange={onTimeChange}
        format={format}
        showSeconds={showSeconds}
        minuteStep={minuteStep}
        disabled={disabled}
        {...(invalid !== undefined ? { invalid } : {})}
      />
    </div>
  );
}
